import type { FC, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import settings from 'src/settings';
import type { Customizing } from 'src/types/customizing';
import useAppLanguage from 'src/hooks/useAppLanguage';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

export interface CustomizingContextValue {
  appLanguages: string[];
  currentApp?: Customizing;
  loading: boolean;
}

const CustomizingContext = createContext<CustomizingContextValue>({
  appLanguages: [],
  currentApp: undefined,
  loading: false,
});

interface CustomizingProviderProps {
  children?: ReactNode;
}

export const CustomizingProvider: FC<CustomizingProviderProps> = ({
  children,
}) => {
  const appLanguages = useAppLanguage();
  const { i18n } = useTranslation();

  const endPoint = apiPaths.CUSTOMIZING.CUSTOM_CONTENT;
  const {
    data: currentApp,
    isFetching,
    isLoading,
  } = useQuery<Customizing, string>({
    queryKey: [endPoint, i18n.language],
    queryFn: () =>
      HttpService.get(endPoint)
        .then(
          ({ data }) =>
            data?.results?.find(
              (_customizations: Record<string, any>) =>
                _customizations.name === settings.VITE_APP_NAME,
            ) || {},
        )
        .catch(() => Promise.reject()),
  });
  const loading = isFetching || isLoading;

  return (
    <CustomizingContext.Provider value={{ currentApp, appLanguages, loading }}>
      {children}
    </CustomizingContext.Provider>
  );
};

export const useCustomizing = () => useContext(CustomizingContext);
